import {
  ARCADE_BASE_POINTS,
  ARCADE_COMBO_MAX_MULT,
  ARCADE_COMBO_STEP,
  ARCADE_EDGE_MULT,
  ARCADE_THRUST_CLEAN_FRAC,
  ARCADE_THRUST_MULT,
  EDGE_QUALITY,
  type EdgeQuality,
} from '../config/constants';

/**
 * Pure combo/scoring core for the arcade ("Sfida") mode. The runtime threads a
 * {@link ArcadeComboState} through each resolved target; everything here is order-
 * dependent but side-effect free, so it can be unit-tested in isolation.
 */
export type ArcadeComboState = {
  score: number;
  /** Current consecutive clean-hit streak (resets to 0 on a hard miss). */
  combo: number;
  /** Highest combo reached so far in the run. */
  maxCombo: number;
};

export const initialComboState: ArcadeComboState = { score: 0, combo: 0, maxCombo: 0 };

/**
 * How a resolved target scored (technique-neutral):
 * - `clean`:   a good hit (cut: correct direction + good/ok edge; thrust: centered) → grows the combo, full points.
 * - `sloppy`:  a weak hit (cut: correct direction + flat edge; thrust: out toward the rim) → keeps the combo, reduced points.
 * - `wrong`:   wrong cut direction → hard miss, resets the combo. (Thrusts have no direction, so never `wrong`.)
 * - `timeout`: TTL expired with no qualifying hit → hard miss, resets the combo.
 */
export type Outcome = 'clean' | 'sloppy' | 'wrong' | 'timeout';

/** @deprecated alias kept while call sites migrate to the technique-neutral {@link Outcome}. */
export type CutOutcome = Outcome;

/** Map a (direction-matched?, edge-quality) pair to a scoring outcome. */
export function classifyCut(matched: boolean, quality: EdgeQuality): CutOutcome {
  if (!matched) return 'wrong';
  return quality === EDGE_QUALITY.flat ? 'sloppy' : 'clean';
}

/**
 * Combo multiplier for a given streak: `1 + floor(combo / step)`, capped. The value
 * reflects the streak *entering* a hit, so points are banked at the at-risk multiplier
 * before the streak grows.
 */
export function comboMultiplier(combo: number): number {
  return Math.min(ARCADE_COMBO_MAX_MULT, 1 + Math.floor(combo / ARCADE_COMBO_STEP));
}

/**
 * Whether the read cut direction matches the required one within tolerance. `null`
 * (direction undecidable — cut too close to the viewing axis) never matches. Handles
 * the 0/360 wraparound; angles are deg, 0 = down, clockwise (frontalProjection convention).
 */
export function directionMatches(
  cutAngleDeg: number | null,
  requiredDeg: number,
  toleranceDeg: number,
): boolean {
  if (cutAngleDeg == null || !isFinite(cutAngleDeg)) return false;
  let d = Math.abs((((cutAngleDeg - requiredDeg) % 360) + 360) % 360);
  if (d > 180) d = 360 - d;
  return d <= toleranceDeg;
}

/**
 * Apply one resolved target to the score state. Pure: returns the next state and the points
 * it gained. `subMult` is the technique's quality sub-multiplier (edge for cuts, centering for
 * thrusts) and is only read for hit outcomes (clean/sloppy). Shared by both techniques so the
 * combo-at-risk semantics stay identical.
 */
export function applyResolution(
  state: ArcadeComboState,
  outcome: Outcome,
  subMult: number,
): { state: ArcadeComboState; pointsGained: number } {
  if (outcome === 'wrong' || outcome === 'timeout') {
    return { state: { ...state, combo: 0 }, pointsGained: 0 };
  }
  // Hit: the multiplier reflects the streak entering this hit (combo-at-risk).
  const pointsGained = Math.round(ARCADE_BASE_POINTS * comboMultiplier(state.combo) * subMult);
  // A clean hit grows the combo; a sloppy one keeps it where it is.
  const combo = outcome === 'clean' ? state.combo + 1 : state.combo;
  const maxCombo = Math.max(state.maxCombo, combo);
  return { state: { score: state.score + pointsGained, combo, maxCombo }, pointsGained };
}

/** Apply a resolved cut: the sub-multiplier comes from the edge quality. */
export function applyCut(
  state: ArcadeComboState,
  outcome: Outcome,
  quality: EdgeQuality,
): { state: ArcadeComboState; pointsGained: number } {
  return applyResolution(state, outcome, ARCADE_EDGE_MULT[quality]);
}

/** Classify a thrust hit by how close to the disc center it landed (centering = quality). */
export function classifyThrust(
  distanceFromCenterM: number,
  ringRadiusM: number,
): 'clean' | 'sloppy' {
  return distanceFromCenterM <= ringRadiusM * ARCADE_THRUST_CLEAN_FRAC ? 'clean' : 'sloppy';
}

/** Apply a resolved thrust: the sub-multiplier comes from the centering tier. */
export function applyThrust(
  state: ArcadeComboState,
  outcome: Outcome,
): { state: ArcadeComboState; pointsGained: number } {
  const subMult = outcome === 'clean' || outcome === 'sloppy' ? ARCADE_THRUST_MULT[outcome] : 0;
  return applyResolution(state, outcome, subMult);
}
